/**
 * MockUp Gen — Generation Service (client-side)
 *
 * Chiama l'endpoint serverless /api/generate che gestisce:
 * - Auth JWT (obbligatorio)
 * - Export allowance (rate limiting)
 * - Generazione via Google Gemini 2.5 Flash Image (API key server-side, mai esposta al client)
 *
 * SICUREZZA: Nessuna API key in questo file. Nessuna chiamata diretta a Google AI.
 */

import { GraphicPanel, VehicleDetails } from '../types';

/**
 * Legge un valore dal cookie storage con supporto chunking.
 * L'auth usa cookieDomain (.forma.frankriccidesign.com) → sessione nei cookie, non localStorage.
 */
function getCookieValue(key: string): string | null {
  function getRaw(name: string): string | null {
    const match = document.cookie.match(
      new RegExp('(?:^|; )' + name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '=([^;]*)')
    );
    return match ? match[1] : null;
  }

  const single = getRaw(key);
  if (single !== null) return decodeURIComponent(single);

  const chunks: string[] = [];
  for (let i = 0; ; i++) {
    const chunk = getRaw(`${key}.${i}`);
    if (chunk === null) break;
    chunks.push(chunk);
  }
  if (chunks.length === 0) return null;
  return decodeURIComponent(chunks.join(''));
}

function getAccessToken(): string | null {
  try {
    const raw = getCookieValue('forma-studio-auth');
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed?.access_token || null;
  } catch {
    return null;
  }
}

export const generateCarMockup = async (
  view: { id: string; label: string },
  panels: GraphicPanel[],
  vehicle: VehicleDetails,
  scenarioPrompt: string,
  carPhotos: { base64: string; angle: string }[] = [],
  previousMockups: { viewName: string; base64?: string }[] = [],
): Promise<{ imageUrl: string; storageUrl: string }> => {

  const accessToken = getAccessToken();

  const panelPayload = panels
    .filter(p => p.base64)
    .map(p => ({
      base64: p.base64,
      type: p.type,
    }));

  const carPhotoPayload = (carPhotos || [])
    .filter(p => p.base64)
    .map(p => ({
      base64: p.base64,
      angle: p.angle,
    }));

  const prevPayload = (previousMockups || []).map(m => ({
    viewName: m.viewName,
    base64: m.base64 || '',
  }));

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
  }

  const response = await fetch('/api/generate', {
    method: 'POST',
    headers,
    body: JSON.stringify({
      panels: panelPayload,
      vehicleDetails: vehicle,
      scenarioPrompt,
      viewId: view.id,
      viewLabel: view.label,
      carPhotos: carPhotoPayload,
      previousMockups: prevPayload,
    }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    const errorMessage = errorData.error || `Generation failed (${response.status})`;
    throw new Error(errorMessage);
  }

  const data = await response.json();

  if (!data.imageBase64) {
    throw new Error('No image data received from server.');
  }

  return {
    imageUrl: `data:image/png;base64,${data.imageBase64}`,
    storageUrl: data.storageUrl || '',
  };
};
